import { getSupabaseClient } from "../providers/supabase";
export interface CreateCheckoutSessionRequest {
  quiz_id: number;
  email: string;
}


// These calls use fetch(), not the shared axios client, so they must attach the login
// token themselves. subscription-status / customer-portal / cancel-subscription require
// login and ownership on the backend (ht-backend app/utils/access_policy.py).
const authHeaders = async (): Promise<Record<string, string>> => {
  const { data: { session } } = await getSupabaseClient().auth.getSession();
  return session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {};
};

export interface CreateCheckoutSessionResponse {
  checkout_url: string;
  session_id: string;
}

export interface PaymentStatusResponse {
  status: 'not_started' | 'pending' | 'processing' | 'completed';
  transaction_id?: string;
}

export interface SubscriptionStatusResponse {
  status: 'not_found' | 'active' | 'cancelled' | 'past_due' | 'incomplete';
  subscription_id?: string;
  current_period_start?: string;
  current_period_end?: string;
  customer_id?: string;
}

export interface CancelSubscriptionRequest {
  subscription_id: string;
}

export interface CustomerPortalRequest {
  customer_id: string;
  return_url?: string;
}

export interface CategorySection {
  Subtext: string;
  Overview: string;
  FamousExample: string[];
  DominantTraits: string[];
  Strengths: string[];
  Weaknesses: string[];
  GrowthOpportunities: string[];
  BehavioralConcepts: string[];
}

export interface SpecificCategorySection {
  Overview: string;
  FamousExample: string[]; // first idx is name, second is paragraph
  DominantTraits: string[];
  Strengths: string[];
  Weaknesses: string[];
  GrowthOpportunities: string[];
  BehavioralConcepts: string[];
}

export interface CategoryMappings {
  [key: string]: CategorySection;
}

export interface SpecificCategoryMappings {
  [key: string]: SpecificCategorySection;
}

// Enhanced error handling for API calls
const handleApiError = (error: any, operation: string) => {
  console.error(`❌ ${operation} failed:`, error);
  
  if (error.response) {
    // Server responded with error status
    const status = error.response.status;
    const message = error.response.data?.message || error.response.data?.detail || `${operation} failed`;
    
    if (status === 404) {
      throw new Error(`Not found: ${message}`);
    } else if (status === 401) {
      throw new Error(`Unauthorized: ${message}`);
    } else if (status === 403) {
      throw new Error(`Forbidden: ${message}`);
    } else if (status >= 500) {
      throw new Error(`Server error: ${message}`);
    } else {
      throw new Error(message);
    }
  } else if (error.request) {
    // Network error
    throw new Error(`Network Error: Please check your internet connection and try again.`);
  } else {
    // Something else happened
    throw new Error(`${operation} failed: ${error.message || 'Unknown error'}`);
  }
};

// Enhanced function to create checkout session
export const createCheckoutSession = async (data: CreateCheckoutSessionRequest): Promise<CreateCheckoutSessionResponse> => {
  try {
    console.log("💳 Creating checkout session for subscription");
    console.log("sending data", JSON.stringify(data));
    
    if (!data.quiz_id || !data.email) {
      throw new Error('Quiz ID and email are required');
    }
    
    const response = await fetch(`${process.env.REACT_APP_API_URL}/stripe/create-checkout-session`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to create checkout session: ${response.statusText} - ${errorText}`);
    }
    
    const result = await response.json();
    console.log("✅ Checkout session created successfully:", result);
    return result;
  } catch (error: any) {
    handleApiError(error, 'Create checkout session');
    throw error; // This will never be reached due to handleApiError throwing, but keeps TypeScript happy
  }
};

// Enhanced function to get payment status
export const getPaymentStatus = async (sessionId: string): Promise<PaymentStatusResponse> => {
  try {
    console.log(`🔍 Getting payment status for session: ${sessionId}`);
    
    if (!sessionId) {
      throw new Error('Session ID is required');
    }
    
    const url = `${process.env.REACT_APP_API_URL}/stripe/payment-status/${sessionId}`;
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to get payment status: ${response.statusText} - ${errorText}`);
    }
    
    const result = await response.json();
    console.log(`✅ Payment status received:`, result);
    return result;
  } catch (error: any) {
    handleApiError(error, 'Get payment status');
    throw error; // This will never be reached due to handleApiError throwing, but keeps TypeScript happy
  }
};

// Enhanced function to get subscription status
export const getSubscriptionStatus = async (customerId: string): Promise<SubscriptionStatusResponse> => {
  try {
    console.log(`🔄 Getting subscription status for customer: ${customerId}`);
    
    if (!customerId) {
      throw new Error('Customer ID is required');
    }
    
    const url = `${process.env.REACT_APP_API_URL}/stripe/subscription-status/${customerId}`;
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        ...(await authHeaders()),
        'Content-Type': 'application/json',
      },
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to get subscription status: ${response.statusText} - ${errorText}`);
    }
    
    const result = await response.json();
    console.log(`✅ Subscription status received:`, result);
    return result;
  } catch (error: any) {
    handleApiError(error, 'Get subscription status');
    throw error; // This will never be reached due to handleApiError throwing, but keeps TypeScript happy
  }
};

// Enhanced function to cancel subscription
export const cancelSubscription = async (subscriptionId: string): Promise<{ status: string; message?: string }> => {
  try {
    console.log(`🚫 Cancelling subscription: ${subscriptionId}`);
    
    if (!subscriptionId) {
      throw new Error('Subscription ID is required');
    }
    
    const response = await fetch(`${process.env.REACT_APP_API_URL}/stripe/cancel-subscription`, {
      method: 'POST',
      headers: {
        ...(await authHeaders()),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        subscription_id: subscriptionId
      }),
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to cancel subscription: ${response.statusText} - ${errorText}`);
    }
    
    const result = await response.json();
    console.log(`✅ Subscription cancelled successfully:`, result);
    return result;
  } catch (error: any) {
    handleApiError(error, 'Cancel subscription');
    throw error; // This will never be reached due to handleApiError throwing, but keeps TypeScript happy
  }
};

// Enhanced function to create customer portal
export const createCustomerPortal = async (customerId: string, returnUrl?: string): Promise<{ status: string; portal_url: string }> => {
  try {
    console.log(`🏢 Creating customer portal for customer: ${customerId}`);
    
    if (!customerId) {
      throw new Error('Customer ID is required');
    }
    
    const response = await fetch(`${process.env.REACT_APP_API_URL}/stripe/customer-portal`, {
      method: 'POST',
      headers: {
        ...(await authHeaders()),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        customer_id: customerId,
        return_url: returnUrl
      }),
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to create customer portal: ${response.statusText} - ${errorText}`);
    }
    
    const result = await response.json();
    console.log(`✅ Customer portal created successfully:`, result);
    return result;
  } catch (error: any) {
    handleApiError(error, 'Create customer portal');
    throw error; // This will never be reached due to handleApiError throwing, but keeps TypeScript happy
  }
};

export const cancelPayment = async (sessionId: string): Promise<{ status: string; message: string }> => {
  const response = await fetch(`${process.env.REACT_APP_API_URL}/stripe/cancel-payment/${sessionId}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
  });
  if (!response.ok) {
    throw new Error(`Failed to cancel payment: ${response.statusText}`);
  }
  return response.json();
}; 

// Enhanced function to get transaction ID
export const getTransactionId = async (quizId: number): Promise<{
  status: string;
  transaction_id: string | null;
  subscription_id?: string;
  customer_id?: string;
  type?: 'subscription' | 'purchase';
  error?: string;
}> => {
  try {
    console.log(`🔍 Getting transaction ID for quiz: ${quizId}`);
    
    if (!quizId) {
      throw new Error('Quiz ID is required');
    }
    
    const response = await fetch(`${process.env.REACT_APP_API_URL}/stripe/get-transaction-id/${quizId}`, {
      method: 'GET',
      headers: {
        ...(await authHeaders()),
        'Content-Type': 'application/json',
      }
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to get transaction ID: ${response.statusText} - ${errorText}`);
    }
    
    const result = await response.json();
    console.log(`✅ Transaction ID received:`, result);
    return result;
  } catch (error: any) {
    console.error(`❌ Get transaction ID failed:`, error);
    
    // For transaction ID, we want to return a structured response even on error
    // rather than throwing, since this is used in hooks that expect this format
    return {
      status: 'error',
      transaction_id: null,
      error: error.message || 'Failed to get transaction ID'
    };
  }
};



export const sendPdfEmail = async (
  email: string, 
  quizId: number, 
  quizData: any, 
  transactionId?: string,
  categorySections?: CategoryMappings,
  specificCategorySections?: SpecificCategoryMappings,
  dominantCategory?: string,
  specificDominantCategory?: string,
  pdfUrl?: string
): Promise<{ status: string; message: string }> => {
  // Prepare enhanced quiz data with category details
  const enhancedQuizData = {
    ...quizData,
    categoryDetails: (categorySections && dominantCategory) ? categorySections[dominantCategory] || {} : {},
    specificCategoryDetails: (specificCategorySections && specificDominantCategory) ? specificCategorySections[specificDominantCategory] || {} : {}
  };

  console.log("sending enhanced quiz data to the backend", enhancedQuizData);

  const response = await fetch(`${process.env.REACT_APP_API_URL}/stripe/send-pdf-email`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      email,
      quiz_id: quizId,
      quiz_data: enhancedQuizData,
      transaction_id: transactionId,
      pdf_url: pdfUrl
    }),
  });
  if (!response.ok) {
    throw new Error(`Failed to send PDF email: ${response.statusText}`);
  }
  return response.json();
};

export const trackPDFDownload = async (
  quizId: number,
  email: string,
  filename: string,
  downloadUrl: string
): Promise<{ status: string; message: string }> => {
  try {
    const response = await fetch(`${process.env.REACT_APP_API_URL}/stripe/track-pdf-download`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        quiz_id: quizId,
        email: email,
        filename: filename,
        download_url: downloadUrl
      }),
    });
    
    if (!response.ok) {
      throw new Error(`Failed to track PDF download: ${response.statusText}`);
    }
    
    return response.json();
  } catch (error) {
    console.error('❌ Error tracking PDF download:', error);
    // Don't throw error as this is non-critical
    return { status: 'error', message: `Failed to track: ${error}` };
  }
};